import React, { useState } from 'react';

import { PageMeta } from '../../components';

const c = {
  teal: '#1A9E85',
  tealLight: 'rgba(26,158,133,0.10)',
  tealBorder: 'rgba(26,158,133,0.25)',
  navy: '#0F2033',
  text: '#1B2B3A',
  textMid: '#4A5E6E',
  textLight: '#7A8C99',
  bg: '#F5F9FC',
  white: '#FFFFFF',
  border: '#DDE7EE',
};

const FAQS = [
  {
    q: 'What is SkipTheDealerships.ai?',
    a:
      'SkipTheDealerships.ai is a direct-to-public online marketplace that connects verified buyers and sellers of vehicles, including private owners and licensed dealers. We provide a digital platform and tools to help people list, discover, and transact on vehicles more safely and efficiently — but we do not operate as a dealership ourselves.',
  },
  {
    q: 'Do you buy, sell, or stock vehicles?',
    a:
      'No. SkipTheDealerships.ai does not stock, buy, or take ownership of any vehicles. We never hold title to vehicles and we do not act as the seller or buyer in transactions. All vehicles listed on our platform are offered directly by private individuals or dealers, and all agreements are made between those parties.',
  },
  {
    q: 'What is your role in a vehicle sale?',
    a: null,
    bullets: [
      'AI‑assisted valuation and pricing insights',
      'Fraud and risk checks, identity verification / KYC workflows',
      'Listing tools (photos, descriptions, disclosures)',
      'Matching and recommendation tools for buyers',
      'Integrations with third‑party partners for financing, payment, or related services',
    ],
    intro:
      'Our role is to provide digital tools and services that make the process of selling and buying vehicles easier, safer, and more transparent. These services may include:',
    outro: 'We are a platform and services provider, not a party to the sale.',
  },
  {
    q: 'Do you guarantee vehicle condition, title, or transaction outcomes?',
    a:
      'No. While our AI tools and verification processes are designed to reduce risk and improve transparency, we do not guarantee vehicle condition, history, title status, or the outcome of any transaction. All information about a vehicle — including condition and history — is provided by the seller or third‑party data sources, and should be independently verified by the buyer.',
  },
  {
    q: 'Who is responsible for the transaction?',
    a: null,
    intro:
      'The buyer and seller (or dealer) are fully responsible for negotiating, agreeing to, and completing the transaction, including payment, transfer of ownership, and any after‑sale issues. SkipTheDealerships.ai does not assume responsibility or liability for:',
    bullets: [
      'The accuracy or completeness of listings',
      'The performance, condition, or safety of any vehicle',
      'The conduct or performance of buyers, sellers, or dealers',
      'Any disputes arising from the transaction',
    ],
    outro:
      'Where we facilitate payments or introduce third‑party services, those services are provided under their own terms and conditions.',
  },
  {
    q: 'How does your AI work?',
    a: null,
    intro:
      'Our AI operates as an internal intelligence layer supporting the marketplace experience. For example, it may:',
    bullets: [
      'Help sellers with price suggestions, listing guidance, and fraud‑pattern checks',
      'Help buyers with personalised search, recommendations, and risk flags',
      'Help dealers with better lead insights and buyer intent scoring',
    ],
    outro:
      'These AI‑driven features are decision‑support tools only. All final decisions about listing, buying, pricing, and negotiation are made by the users — not by SkipTheDealerships.ai.',
  },
  {
    q: 'How do you handle compliance, money, and financing?',
    a: null,
    intro: 'As a marketplace, our main responsibilities relate to:',
    bullets: [
      'User verification and fraud prevention',
      'Safe handling of payments where we provide payment rails',
      'Clear disclosure of our role, fees, and relationships with third‑party partners',
    ],
    outro:
      'When we integrate financing, insurance, or banking services, the lenders and financial providers remain the regulated entities that approve and fund transactions. Our fees and commissions are disclosed, and we act as a platform connecting users to these services — not as a lender ourselves.',
  },
  {
    q: 'Are you a dealership or broker?',
    a: null,
    intro: 'No. SkipTheDealerships.ai is not a dealership, broker, or reseller. We do not:',
    bullets: [
      'Hold vehicle inventory',
      'Sign sales contracts on behalf of users',
      'Provide legal, tax, or financial advice',
    ],
    outro:
      'We are a technology and marketplace company offering access, tools, and services to support safer and more efficient vehicle transactions.',
  },
];

function FaqItem({ item, index }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        borderBottom: `1px solid ${c.border}`,
        transition: 'background 0.2s',
      }}
    >
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '24px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 20,
        }}
        aria-expanded={open}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <span
            style={{
              flexShrink: 0,
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: open ? c.teal : c.bg,
              border: `1px solid ${open ? c.teal : c.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 800,
              color: open ? '#fff' : c.textLight,
              transition: 'all 0.2s',
              marginTop: 1,
            }}
          >
            {String(index + 1).padStart(2, '0')}
          </span>
          <span
            style={{
              fontSize: 17,
              fontWeight: 700,
              color: open ? c.teal : c.navy,
              lineHeight: 1.45,
              transition: 'color 0.2s',
            }}
          >
            {item.q}
          </span>
        </div>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          style={{
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.25s',
            color: open ? c.teal : c.textLight,
          }}
        >
          <path
            d="M5 8l5 5 5-5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div style={{ paddingBottom: 28, paddingLeft: 44 }}>
          {item.intro && (
            <p style={{ fontSize: 15, color: c.textMid, lineHeight: 1.8, margin: '0 0 14px' }}>
              {item.intro}
            </p>
          )}
          {item.a && (
            <p style={{ fontSize: 15, color: c.textMid, lineHeight: 1.8, margin: 0 }}>{item.a}</p>
          )}
          {item.bullets && (
            <ul style={{ margin: '0 0 14px', padding: 0, listStyle: 'none' }}>
              {item.bullets.map((b, i) => (
                <li
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    fontSize: 15,
                    color: c.textMid,
                    lineHeight: 1.7,
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: c.teal,
                      marginTop: 8,
                    }}
                  />
                  {b}
                </li>
              ))}
            </ul>
          )}
          {item.outro && (
            <p
              style={{
                fontSize: 15,
                color: c.textMid,
                lineHeight: 1.8,
                margin: 0,
                fontStyle: 'italic',
              }}
            >
              {item.outro}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function FaqPage() {
  return (
    <>
      <PageMeta
        path="/faq"
        title="Frequently Asked Questions"
        description="Everything you need to know about how SkipTheDealerships.ai works — who we are, what we do, and what we don't."
      />
      <div
        style={{
          fontFamily: 'DM Sans, system-ui, sans-serif',
          color: c.text,
          background: c.white,
          minHeight: '100vh',
        }}
      >
        {/* Hero */}
        <section
          style={{
            background: `linear-gradient(160deg, ${c.navy} 0%, #1B3A52 100%)`,
            padding: 'clamp(72px,9vw,120px) 32px clamp(60px,8vw,100px)',
          }}
        >
          <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
            <span
              style={{
                display: 'inline-block',
                background: 'rgba(26,158,133,0.15)',
                border: '1px solid rgba(26,158,133,0.35)',
                borderRadius: 20,
                padding: '5px 16px',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.09em',
                textTransform: 'uppercase',
                color: c.teal,
                marginBottom: 20,
              }}
            >
              How it works
            </span>
            <h1
              style={{
                fontSize: 'clamp(32px,5vw,56px)',
                fontWeight: 800,
                color: '#fff',
                lineHeight: 1.1,
                letterSpacing: '-1px',
                margin: '0 0 20px',
              }}
            >
              Frequently Asked Questions
            </h1>
            <p
              style={{ fontSize: 18, color: 'rgba(255,255,255,0.65)', lineHeight: 1.75, margin: 0 }}
            >
              Everything you need to know about how SkipTheDealerships.ai works — who we are, what
              we do, and what we don't.
            </p>
          </div>
        </section>

        {/* FAQ list */}
        <section style={{ padding: 'clamp(60px,8vw,100px) 32px' }}>
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            {FAQS.map((item, i) => (
              <FaqItem key={i} item={item} index={i} />
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        <section
          style={{
            background: c.bg,
            borderTop: `1px solid ${c.border}`,
            padding: '60px 32px',
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: 560, margin: '0 auto' }}>
            <h2
              style={{
                fontSize: 'clamp(22px,3vw,32px)',
                fontWeight: 700,
                color: c.navy,
                margin: '0 0 12px',
              }}
            >
              Still have questions?
            </h2>
            <p style={{ fontSize: 16, color: c.textMid, lineHeight: 1.75, margin: '0 0 28px' }}>
              Our team is happy to walk you through anything — whether you're a buyer, seller, or
              dealer exploring the platform.
            </p>
            <a
              href="mailto:support@skipthedealerships.ca"
              style={{
                display: 'inline-block',
                background: c.teal,
                color: '#fff',
                fontWeight: 700,
                fontSize: 15,
                padding: '14px 28px',
                borderRadius: 12,
                textDecoration: 'none',
                boxShadow: '0 4px 16px rgba(26,158,133,0.3)',
              }}
            >
              Email Support →
            </a>
          </div>
        </section>

        {/* Footer note */}
        <div style={{ background: c.navy, padding: '24px 32px', textAlign: 'center' }}>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', margin: 0 }}>
            &copy; SkipTheDealerships.ai — Drivana Technologies Inc. &nbsp;·&nbsp; Canadian dealers
            only &nbsp;·&nbsp; Not a dealership, broker, or lender.
          </p>
        </div>
      </div>
    </>
  );
}
